import { type JSX, createMemo, Show } from "solid-js";
import { createReloading } from "./reloading";
import usePage from "./usePage";

// Plain JSX, deliberately not `JSX.Element | (() => JSX.Element)`: the union
// invites a "render prop or value?" test, and that test cannot be written. See
// the render below.
interface DeferredProps {
  children: JSX.Element;
  fallback: JSX.Element;
  data: string | string[];
}

function Deferred(props: DeferredProps): JSX.Element {
  if (!props.data) {
    throw new Error("`<Deferred>` requires a `data` prop to be a string or array of strings");
  }

  const page = usePage();
  const keys = createMemo(() => (Array.isArray(props.data) ? props.data : [props.data]));

  // The fallback shows while a visit re-fetching one of our keys is in flight, and goes
  // away when it finishes (createReloading says why that is the visit, not the value).
  const reloading = createReloading(keys);

  const loaded = createMemo(
    () => !reloading() && keys().every((key) => page.props[key] !== undefined),
  );

  return (
    // `children` and `fallback` are handed to <Show> untouched, and in particular
    // NOT unwrapped with `typeof x === "function" ? x() : x`. That test is true for
    // far more than a render prop: every Solid control-flow component returns a memo,
    // so `<Deferred><For .../></Deferred>` hands us a function too — and calling it
    // reads the memo ONCE and inserts the snapshot, freezing the whole subtree at
    // whatever the props said the moment <Show> flipped. Everything below then
    // ignores later prop updates, silently and without erroring.
    //
    // <Show> and `insert` already handle both shapes: a zero-arg function child is
    // called inside an effect, so a render prop stays lazy (the getter is still only
    // read once `loaded()` is true) and a memo stays live.
    <Show when={loaded()} fallback={props.fallback}>
      {props.children}
    </Show>
  );
}

export default Deferred;
